import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { UserContext } from "../../shared/context/userContext";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import EMPTextAreaField from "../../shared/components/EMPTextAreaField/EMPTextAreaField";
import EMPInputField from "../../shared/components/EMPInputField/EMPInputField";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import DealerOnboardingClient from "../../clients/DealerOnboardingClient";
import styles from "./DealerInformation.module.scss";
import { formatPhoneNumber } from "../../utils/FormatPhoneNumber";
import { DEALER_ONBOARDING_PROGRESS_QUERY_KEY } from "../../shared/utils/onboardingProgressUtil";

const DealerInformation = ({ dealerInfo }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const queryClient = useQueryClient();

  // Extracting user/dealer info for the API
  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const userId = user?.userId || user?.commonName;

  const normalizeApiPhone = (raw) => {
    const digits = (raw ?? "")
      .replace(/\D/g, "")
      .replace(/^1/, "")
      .slice(0, 10);
    return digits.length === 10 ? formatPhoneNumber(digits) : "";
  };

  const [savedApiPhone, setSavedApiPhone] = useState(() =>
    normalizeApiPhone(dealerInfo?.phoneNumber)
  );

  useEffect(() => {
    setSavedApiPhone(normalizeApiPhone(dealerInfo?.phoneNumber));
  }, [dealerInfo?.phoneNumber]);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (dealerInfo?.phoneNumber && !phoneNumber) {
      const digits = dealerInfo.phoneNumber
        .replace(/\D/g, "")
        .replace(/^1/, "")
        .slice(0, 10);
      if (digits.length === 10) {
        setPhoneNumber(formatPhoneNumber(digits));
        setIsPhoneNumberValid(true);
      }
    }
  }, [dealerInfo?.phoneNumber, phoneNumber]);

  const savePhoneMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.updateDealerPhoneNumber(dealerCommonId, payload),
    onSuccess: () => {
      setPhoneError("");
      setSavedApiPhone(phoneNumber);
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: () => {
      setPhoneError(t("DealerOnboarding.error.savePhoneFailed"));
    },
  });

  const isSubmitting = savePhoneMutation.isPending;

  const handlePhoneChange = (e) => {
    const input = e.target.value;

    let newDigits = input.replace(/\D/g, "");
    if (input.startsWith("+1") && newDigits.startsWith("1")) {
      newDigits = newDigits.slice(1);
    }

    const oldDigits = phoneNumber.replace(/\D/g, "").replace(/^1/, "");
    if (
      input.length < phoneNumber.length &&
      newDigits === oldDigits &&
      newDigits.length > 0
    ) {
      newDigits = newDigits.slice(0, -1);
    }

    const finalDigits = newDigits.slice(0, 10);
    const isValid = finalDigits.length === 10;
    setIsPhoneNumberValid(isValid);
    setPhoneNumber(formatPhoneNumber(finalDigits));

    const hasInvalidChars = /[^0-9()+\-\s]/.test(input);
    if (hasInvalidChars) {
      setPhoneError("Phone number must contain numbers only.");
    } else if (finalDigits.length > 0 && !isValid) {
      setPhoneError("Please enter a valid phone number.");
    } else {
      setPhoneError("");
    }
  };

  const handleSavePhoneClick = () => {
    if (!isPhoneNumberValid) {
      setPhoneError("Phone number must be exactly 10 digits.");
      return;
    }

    const rawDigits = phoneNumber.replace(/\D/g, "");
    const backendValue = `+${rawDigits}`;

    const payload = {
      phoneNumber: backendValue,
      updatedBy: userId,
    };

    savePhoneMutation.mutate(payload);
  };

  let dealerNameAndAddressValue = "";
  if (dealerInfo) {
    dealerNameAndAddressValue = `${dealerInfo?.dealerName}\n${dealerInfo?.addressStreet1},\n${dealerInfo?.addressCity}, ${dealerInfo?.addressState} ${dealerInfo?.addressZip}`;
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.introduction}>
        <Typography variant="body1" className={styles.introductionText}>
          {t("DealerOnboarding.content.dealerInformation.description")}
        </Typography>
      </Box>

      <Box className={styles.formRow}>
        <Box className={styles.addressRow}>
          <Typography className={styles.fieldLabel}>
            {t("DealerOnboarding.content.dealerInformation.addressBoxLabel")}
          </Typography>
          <EMPTextAreaField
            value={dealerNameAndAddressValue}
            disabled
            multiline
            minRows={4}
            className={styles.addressTextArea}
          />
        </Box>

        <Box className={styles.column}>
          <Typography className={styles.fieldLabel}>
            {t("DealerOnboarding.content.dealerInformation.phoneBoxLabel")}
          </Typography>
          <Box className={styles.phoneInputRow}>
            <Box className={styles.phoneFieldContainer}>
              <EMPInputField
                placeholder="+1 (---) --- - ----"
                value={phoneNumber}
                onChange={handlePhoneChange}
                className={styles.phoneInput}
                disabled={isSubmitting}
                inputProps={{
                  maxLength: 17,
                  inputMode: "numeric",
                }}
              />
              {phoneError && (
                <Typography className={styles.phoneErrorText}>
                  {phoneError}
                </Typography>
              )}
            </Box>
            <EMPButton
              variant="contained"
              testId="save-dealer-phone-button"
              onClick={handleSavePhoneClick}
              isFord={isFordBrand}
              className={styles.savePhoneButton}
              disabled={
                !isPhoneNumberValid ||
                isSubmitting ||
                phoneNumber === savedApiPhone
              }
            >
              {isSubmitting
                ? t("Saving")
                : t(
                    "DealerOnboarding.content.dealerInformation.savePhoneButtonLabel"
                  )}
            </EMPButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

DealerInformation.propTypes = {
  dealerInfo: PropTypes.shape({
    dealerName: PropTypes.string,
    addressStreet1: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    addressZip: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
};

export default DealerInformation;
